import { KYCStatus } from 'features/pots/types';
const DOCUMENT_ACTIONS = [
    'UPLOAD_IDENTITY_DOCUMENT',
    'UPLOAD_PROOF_OF_ADDRESS',
];
// Any rejection with user-facing copy counts, regardless of reason —
// mirroring how the pots/invest funnels read `rejections`.
const hasRejectionCopy = (rejection) => !!rejection.rejectionReasonString;
const isDocumentAction = (action) => !!action && DOCUMENT_ACTIONS.includes(action);
const normalize = (source, status, requiredUserAction, rejections) => {
    const rejection = rejections?.find(hasRejectionCopy);
    const documentAction = isDocumentAction(requiredUserAction)
        ? requiredUserAction
        : undefined;
    if (rejection) {
        return { source, status, requiredUserAction: documentAction, rejection };
    }
    if (documentAction) {
        return { source, status, requiredUserAction: documentAction };
    }
    // A verification blocked on a non-document step (disclosures, bank-transfer
    // top-up…) is not actionable from the credit-score flow — fall back to the
    // address-status modals rather than showing a wait-forever pending modal.
    if (requiredUserAction || rejections?.length)
        return undefined;
    if (status === 'PENDING')
        return { source, status };
    return undefined;
};
const isActionable = (verification) => !!verification?.rejection || !!verification?.requiredUserAction;
/**
 * The user-level KYC verification state relevant to the credit-score gate,
 * normalised across the two product payloads that carry it: the pots/wallet
 * account (nested `kycVerification`) and, for invest-only users, the trading
 * account (top-level `requiredUserAction`/`rejections`). The two payloads
 * project different verification records of the same per-user checks, so
 * they can disagree: an actionable state (a rejection or a document upload)
 * wins over one that is merely pending, with pots preferred on a tie.
 * Returns undefined when there is nothing document-shaped to act on; callers
 * should then fall back to `address.kycStatus`.
 *
 * Both slices are refreshed by `updateWealthAccounts()` on cold launch.
 */
const selectCreditScoreKycVerification = (state) => {
    const potsKyc = state.pots?.account?.kycVerification;
    const fromPots = potsKyc && potsKyc.status !== KYCStatus.Success
        ? normalize('pots', potsKyc.status === KYCStatus.Fail ? 'FAIL' : 'PENDING', potsKyc.requiredUserAction, potsKyc.rejections)
        : undefined;
    const investAccounts = state.invest?.accounts?.map(({ account }) => account);
    // KYC is user-level and never re-runs once passed: an account only reaches
    // ACTIVE (or CLOSED, which requires having been opened) after the user's
    // verification succeeded. An identity-check status alongside one is noise
    // from a subsequent account opening, not a real KYC blocker.
    const hasKycPassedAccount = investAccounts?.some((account) => account.status === 'ACTIVE' || account.status === 'CLOSED');
    const investVerifications = hasKycPassedAccount
        ? undefined
        : investAccounts
            ?.filter((account) => account.status === 'IDENTITY_CHECKS_IN_PROGRESS' ||
            account.status === 'IDENTITY_CHECKS_NEED_USER_ACTION')
            .map((account) => normalize('invest', 'PENDING', account.requiredUserAction, account.rejections))
            .filter((verification) => !!verification);
    const fromInvest = investVerifications?.find(isActionable) ?? investVerifications?.[0];
    // A verification with something actionable must not be shadowed by one
    // that is merely pending — a non-actionable pots state must not hide an
    // invest document upload (or vice versa).
    if (isActionable(fromPots))
        return fromPots;
    if (isActionable(fromInvest))
        return fromInvest;
    return fromPots ?? fromInvest;
};
export default selectCreditScoreKycVerification;
